import { FOOTER_GTM_OVERRIDE } from "@jti/ui/eva-components/Footer";

import { logoInputs } from "@/components/Logo/inputs/logoInputs.builder";

const navLinkInputs = [
  {
    name: "title",
    type: "string",
    required: true,
  },
  {
    name: "url",
    type: "url",
    required: true,
  },
  {
    name: "isActive",
    type: "boolean",
  },
  {
    name: "gtmOverride",
    type: "string",
    enum: FOOTER_GTM_OVERRIDE,
  },
] as const satisfies Inputs;

export const footerInputs = [
  {
    name: "logoProps",
    type: "object",
    required: true,
    subFields: logoInputs,
  },
  {
    name: "showUpdatedFooterUI",
    type: "boolean",
    defaultValue: false,
    onChange: (options) => {
      const value = options.get("showUpdatedFooterUI");
      const footerNavProps = options.get("footerNavProps") as Map<string, unknown>;

      if (value) {
        //If turn on and don't have previous state then skip
        if (!footerNavProps.get("backup_isFooterNavigationCollapsible")) return;

        //If turn on UI changes and have previous state then load all backup data
        footerNavProps.set("isFooterNavigationCollapsible", footerNavProps.get("backup_isFooterNavigationCollapsible"));
        footerNavProps.set("multipleCollapseSections", footerNavProps.get("backup_multipleCollapseSections"));
        footerNavProps.set("areSectionsOpenByDefault", footerNavProps.get("backup_areSectionsOpenByDefault"));
        footerNavProps.set("multipleColumnList", footerNavProps.get("backup_multipleColumnList"));
        footerNavProps.set("rowSize", footerNavProps.get("backup_rowSize"));

        options.set("socialTitle", options.get("backup_socialTitle"));
        options.set("paymentTitle", options.get("backup_paymentTitle"));
        options.set("hideFirstBottomZone", options.get("backup_hideFirstBottomZone"));
        options.set("firstBottomZoneTitle", options.get("backup_firstBottomZoneTitle"));
        options.set("hideSecondBottomZone", options.get("backup_hideSecondBottomZone"));
        options.set("secondBottomZoneTitle", options.get("backup_secondBottomZoneTitle"));
      } else {
        //If turn off UI changes then save all current state
        footerNavProps.set("backup_isFooterNavigationCollapsible", footerNavProps.get("isFooterNavigationCollapsible"));
        footerNavProps.set("backup_multipleCollapseSections", footerNavProps.get("multipleCollapseSections"));
        footerNavProps.set("backup_areSectionsOpenByDefault", footerNavProps.get("areSectionsOpenByDefault"));
        footerNavProps.set("backup_multipleColumnList", footerNavProps.get("multipleColumnList"));
        footerNavProps.set("backup_rowSize", footerNavProps.get("rowSize"));

        footerNavProps.set("isFooterNavigationCollapsible", false);
        footerNavProps.set("multipleCollapseSections", false);
        footerNavProps.set("areSectionsOpenByDefault", false);
        footerNavProps.set("multipleColumnList", false);
        footerNavProps.set("rowSize", null);

        options.set("backup_socialTitle", options.get("socialTitle"));
        options.set("backup_paymentTitle", options.get("paymentTitle"));
        options.set("backup_hideFirstBottomZone", options.get("hideFirstBottomZone"));
        options.set("backup_firstBottomZoneTitle", options.get("firstBottomZoneTitle"));
        options.set("backup_hideSecondBottomZone", options.get("hideSecondBottomZone"));
        options.set("backup_secondBottomZoneTitle", options.get("secondBottomZoneTitle"));

        options.set("socialTitle", "");
        options.set("paymentTitle", "");
        options.set("hideFirstBottomZone", false);
        options.set("firstBottomZoneTitle", "");
        options.set("hideSecondBottomZone", false);
        options.set("secondBottomZoneTitle", "");
      }
    },
  },
  {
    name: "footerNavProps",
    type: "object",
    required: true,
    subFields: [
      {
        name: "isFooterNavigationCollapsible",
        type: "boolean",
        showIf: `parent.get("showUpdatedFooterUI") === true`,
        defaultValue: false,
      },
      {
        name: "multipleCollapseSections",
        type: "boolean",
        showIf: `parent.get("showUpdatedFooterUI") === true`,
        defaultValue: false,
      },
      {
        name: "areSectionsOpenByDefault",
        type: "boolean",
        showIf: `parent.get("showUpdatedFooterUI") === true`,
        defaultValue: false,
      },
      {
        name: "multipleColumnList",
        type: "boolean",
        showIf: `parent.get("showUpdatedFooterUI") === true`,
        defaultValue: false,
      },
      {
        name: "rowSize",
        type: "number",
        showIf: `options.get("multipleColumnList") === true`,
      },
      {
        name: "subNavs",
        type: "list",
        required: true,
        subFields: [
          {
            name: "title",
            type: "string",
            required: true,
            defaultValue: "",
          },
          {
            name: "links",
            type: "list",
            required: true,
            subFields: navLinkInputs,
          },
        ],
      },
      {
        name: "subNavsInlineEndSection",
        type: "object",
        subFields: [
          { name: "title", type: "string", required: true },
          {
            name: "links",
            type: "list",
            required: true,
            subFields: navLinkInputs,
            defaultValue: [],
          },
        ],
        defaultValue: {
          title: "",
          links: [],
        },
      },
    ],
    defaultValue: {
      subNavs: [],
      subNavsInlineEndSection: {
        title: "",
        links: [],
      },
    },
  },
  {
    name: "copyrightProps",
    type: "object",
    required: true,
    subFields: [
      {
        name: "enableCookieSettings",
        type: "boolean",
        required: true,
      },
      {
        name: "copyright",
        type: "string",
        required: true,
      },
      {
        name: "isCurrentYearAppended",
        type: "boolean",
        friendlyName: "Append current year to the copyright",
      },
      {
        name: "links",
        type: "list",
        subFields: navLinkInputs,
        required: true,
      },
    ],
    defaultValue: {
      copyright: "",
      links: [],
      isCurrentYearAppended: true,
      enableCookieSettings: false,
    },
  },
  {
    name: "ariaLabels",
    type: "object",
    subFields: [
      {
        name: "footerCookiesSettingsLink",
        friendlyName: "Cookie Settings Link Aria Label",
        type: "string",
        defaultValue: "Manage cookie settings",
        required: true,
      },
    ],
  },
  {
    name: "flipIconsOrder",
    type: "boolean",
    showIf: `options.get("showUpdatedFooterUI") === true`,
    defaultValue: false,
  },
  {
    name: "paymentTitle",
    type: "string",
    showIf: `options.get("showUpdatedFooterUI") === true`,
    defaultValue: "",
  },
  {
    name: "paymentIcons",
    type: "list",
    subFields: [
      {
        name: "alt",
        type: "string",
        required: true,
      },
      {
        name: "icon",
        type: "file",
        required: true,
        allowedFileTypes: ["svg", "jpg", "png", "gif", "jpeg"],
      },
      {
        name: "width",
        type: "number",
      },
      {
        name: "height",
        type: "number",
      },
      {
        name: "href",
        type: "url",
      },
      {
        name: "target",
        type: "string",
        enum: ["_self", "_blank"],
        defaultValue: "_self",
      },
    ],
    defaultValue: [
      {
        alt: "Visa",
        icon: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F723c8d4e57704e858082b6ba1d7f5e21",
      },
      {
        alt: "Mastercard",
        icon: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F9c258ff6e45b48dca54c2a326b8f8a50",
      },
      {
        alt: "Klarna",
        icon: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fb52247ea2ded478c860ae2d7e2f72002",
      },
    ],
  },
  {
    name: "socialTitle",
    type: "string",
    showIf: `options.get("showUpdatedFooterUI") === true`,
    defaultValue: "",
  },
  {
    name: "socialIcons",
    type: "list",
    subFields: [
      {
        name: "alt",
        type: "string",
        required: true,
      },
      {
        name: "icon",
        type: "file",
        allowedFileTypes: ["svg", "jpg", "png", "gif", "jpeg"],
        required: true,
      },
      {
        name: "link",
        type: "url",
        required: true,
      },
    ],
    defaultValue: [
      {
        alt: "Youtube",
        icon: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F76b294f295d44040858a9536eaa439a9",
        link: "https://youtube.com",
      },
      {
        alt: "Facebook",
        icon: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fbdacad29a20d416487e9d99a0d0940d7",
        link: "https://facebook.com",
      },
      {
        alt: "Instagram",
        icon: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F63665a06a3e7425ab84d4b2b5d8edfa9",
        link: "https://instagram.com",
      },
    ],
  },
  {
    name: "healthBannerProps",
    type: "object",
    subFields: [
      {
        name: "content",
        type: "richText",
        defaultValue: "",
      },
      {
        name: "variant",
        type: "string",
        enum: ["default", "strong", "stronger"],
        defaultValue: "strong",
      },
    ],
  },
  {
    name: "isExtended",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "enableFooterBottomZone",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "hideFirstBottomZone",
    type: "boolean",
    showIf: `options.get("showUpdatedFooterUI") === true`,
    defaultValue: false,
  },
  {
    name: "firstBottomZoneTitle",
    type: "string",
    showIf: `options.get("showUpdatedFooterUI") === true`,
    defaultValue: "",
  },
  {
    name: "hideSecondBottomZone",
    type: "boolean",
    showIf: `options.get("showUpdatedFooterUI") === true`,
    defaultValue: false,
  },
  {
    name: "secondBottomZoneTitle",
    type: "string",
    showIf: `options.get("showUpdatedFooterUI") === true`,
    defaultValue: "",
  },
  {
    name: "firstEditingZone",
    type: "blocks",
    hideFromUI: true,
    defaultValue: [],
  },
  {
    name: "secondEditingZone",
    type: "blocks",
    hideFromUI: true,
    defaultValue: [],
  },
  {
    name: "enableFooterTopZone",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "topEditingZone",
    type: "blocks",
    hideFromUI: true,
    defaultValue: [],
  },
  {
    name: "enableTopBanner",
    type: "boolean",
  },
  {
    name: "topBannerEditingZone",
    type: "blocks",
    hideFromUI: true,
    defaultValue: [],
  },
  {
    name: "withListComponent",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "theme",
    type: "string",
    enum: ["light", "dark"],
    defaultValue: "dark",
  },
  {
    name: "openAssetsInNewTab",
    type: "boolean",
    defaultValue: false,
  },
] as const satisfies Inputs;

export type FooterProps = BuilderComponentProps<typeof footerInputs>;
